import { isNodeTypeEqual } from "@features/builder/helpers/nodeTypeUtils"
import { linkEncode, linkParse } from "@components/NodeLink/linkHelpers"
import { NodeGraphLink } from "@components/NodeLink/types"
import { GraphDetails } from "@features/builder/types"
import { UnpackedGraph } from "@store/services/converter/types"
import { compareLinkedGraphDates } from "@utils/dateValidation"
import { NodesMapper } from "@features/builder/helpers/nodesMapper"
import { NodeType } from "@features/builder/graph/types"
import { getParentsList } from "@utils/node/getParentsList"

const validateSimulationFrequency = (
  currentGraph: GraphDetails | null,
  linkedGraph: UnpackedGraph | undefined,
): string | null => {
  const currentFrequency = currentGraph?.SimulationFrequency
  const linkedFrequency = linkedGraph?.meta.SimulationFrequency

  if (!currentFrequency || !linkedFrequency || currentFrequency === linkedFrequency) return null

  return `Simulation frequency mismatch: linked graph uses ${linkedFrequency.toUpperCase()}, this graph uses ${currentFrequency.toUpperCase()}`
}

const validateDateRange = (
  currentGraph: GraphDetails | null,
  linkedGraph: UnpackedGraph | undefined,
): string | null => {
  if (!linkedGraph) return null

  const dateComparison = compareLinkedGraphDates(
    {
      startDate: currentGraph?.StartDate,
      endDate: currentGraph?.EndDate,
      simulationFrequency: currentGraph?.SimulationFrequency,
      yearlyRollingStartMonth: Number.parseInt(currentGraph?.YearlyRollingStartMonth ?? "1", 10),
      rollingDuration: currentGraph?.RollingHorizon
        ? Number.parseFloat(currentGraph.RollingHorizon)
        : undefined,
      maxLag: 0,
    },
    {
      startDate: linkedGraph?.meta.StartDate,
      endDate: linkedGraph?.meta.EndDate,
      simulationFrequency: linkedGraph?.meta.SimulationFrequency,
      yearlyRollingStartMonth: Number.parseInt(
        linkedGraph?.meta.YearlyRollingStartMonth ?? "1",
        10,
      ),
      rollingDuration: linkedGraph?.meta.RollingHorizon
        ? Number.parseFloat(linkedGraph.meta.RollingHorizon)
        : undefined,
    },
  )

  const hasError =
    dateComparison.errors.startDateMissing ||
    dateComparison.errors.startDateMismatch ||
    dateComparison.errors.endDateMissing ||
    dateComparison.errors.endDateOutOfRange

  if (!hasError) return null

  return `Date range mismatch: linked graph uses ${dateComparison.selected.start ?? "???"} to ${
    dateComparison.selected.end ?? "???"
  }, this graph uses ${dateComparison.current.start ?? "???"} to ${
    dateComparison.current.end ?? "???"
  }`
}

// The link stores the target node's label; resolve it through meta to get the BaseGraphZipNode.
const findLinkedGraphNode = (
  linkedGraph: UnpackedGraph | undefined,
  linkedNodeLabel: string | undefined,
) => {
  const linkedNodeEntry =
    linkedNodeLabel && linkedGraph
      ? Object.entries(linkedGraph.meta.nodes).find(([, meta]) => meta.label === linkedNodeLabel)
      : undefined
  const linkedNodeName = linkedNodeEntry?.[0]

  return linkedNodeName != null
    ? linkedGraph?.graph.nodes.find((n) => n.name === linkedNodeName) ??
        linkedGraph?.graph.attributes?.find((n) => n.name === linkedNodeName)
    : undefined
}

const validateNodeType = (
  linkedGraph: UnpackedGraph | undefined,
  nodeGraphLink: NodeGraphLink | undefined,
  nodesMapper: NodesMapper,
  currentNodeId: string | undefined,
): string | null => {
  const currentNode = currentNodeId ? nodesMapper.nodesMap.get(currentNodeId) : undefined
  const currentNodeType =
    currentNode && "metaNode" in currentNode.data ? currentNode.type : undefined

  const linkedNodeLabel = nodeGraphLink?.node
  const linkedGraphNode = findLinkedGraphNode(linkedGraph, linkedNodeLabel)

  if (
    !currentNodeType ||
    !linkedGraphNode ||
    isNodeTypeEqual(linkedGraphNode.type, currentNodeType)
  )
    return null

  return `Node type mismatch: current node is ${currentNodeType}, but linked node "${linkedNodeLabel}" is ${linkedGraphNode.type}`
}

const validateLinkedNodeExists = (
  linkedGraph: UnpackedGraph | undefined,
  nodeGraphLink: NodeGraphLink | undefined,
  isLoading: boolean,
): string | null => {
  if (!nodeGraphLink || isLoading || !linkedGraph) return null

  const linkedNodeLabel = nodeGraphLink.node
  if (!linkedNodeLabel) return null

  const linkedGraphNode = findLinkedGraphNode(linkedGraph, linkedNodeLabel)
  if (linkedGraphNode) return null

  return `Linked node "${linkedNodeLabel}" does not exist in the linked graph`
}

const validateVersionConflict = (
  nodeGraphLink: NodeGraphLink | undefined,
  nodesMapper: NodesMapper,
): string | null => {
  if (!nodeGraphLink) return null

  const { org, div, prod, family, version } = nodeGraphLink
  const currentFamilyKey = linkEncode(org, div, prod, family)

  // Collect all nodes linking to the same family — group by version, track labels
  const versionToLabels = new Map<string, string[]>()

  for (const [nodeId, node] of nodesMapper.nodesMap) {
    if (!("metaNode" in node.data) || !node.data.metaNode.Link) continue

    const parsed = linkParse(node.data.metaNode.Link)
    if (!parsed) continue
    if (linkEncode(parsed.org, parsed.div, parsed.prod, parsed.family) !== currentFamilyKey)
      continue

    const label = nodesMapper.idToLabelMap.get(nodeId) ?? nodeId
    const labels = versionToLabels.get(parsed.version) ?? []
    labels.push(label)
    versionToLabels.set(parsed.version, labels)
  }

  if (versionToLabels.size <= 1) return null

  const versions = [...versionToLabels.keys()].sort()
  const allLabels = [...versionToLabels.values()].flat().sort()

  return `Multiple versions of "${currentFamilyKey}" are linked: nodes "${allLabels.join(
    '", "',
  )}" must all use the same version, but use (${versions.join(", ")})`
}

const validateNoParentNode = (
  nodesMapper: NodesMapper,
  currentNodeId: string | undefined,
): string | null => {
  if (!currentNodeId) return null

  const currentNode = nodesMapper.nodesMap.get(currentNodeId)
  if (
    !currentNode ||
    !("originalNode" in currentNode.data || "originalAttribute" in currentNode.data)
  )
    return null

  const parentsList = getParentsList(currentNode)

  if (!parentsList.length) return null

  return "Node with a link cannot have a parent node"
}

const validateLinkedGraphExists = (
  linkedGraph: UnpackedGraph | undefined,
  nodeGraphLink: NodeGraphLink | undefined,
  isLoading: boolean,
): string | null => {
  if (!nodeGraphLink) return null
  if (isLoading) return null
  const hasCompleteKey = !!(
    nodeGraphLink.org &&
    nodeGraphLink.div &&
    nodeGraphLink.prod &&
    nodeGraphLink.family &&
    nodeGraphLink.version
  )
  if (!hasCompleteKey) return null
  if (!linkedGraph) return "Linked graph does not exist or could not be loaded"
  return null
}

const validateTableNode = (
  nodesMapper: NodesMapper,
  currentNodeId: string | undefined,
): string | null => {
  if (!currentNodeId) return null

  const currentNode = nodesMapper.nodesMap.get(currentNodeId)
  if (currentNode?.type === NodeType.Table && !!currentNode.data.metaNode.Link)
    return "Table node cannot have a Link"

  return null
}

type GetNodeLinkValidationStateArgs = {
  currentGraph: GraphDetails | null
  linkedGraph?: UnpackedGraph
  link?: NodeGraphLink
  nodesMapper: NodesMapper
  nodeId?: string
  isLinkedGraphLoading?: boolean
}

export const getNodeLinkValidationState = ({
  currentGraph,
  linkedGraph,
  link,
  nodesMapper,
  nodeId,
  isLinkedGraphLoading = false,
}: GetNodeLinkValidationStateArgs) => {
  const errorMessages: string[] = [
    validateTableNode(nodesMapper, nodeId),
    validateLinkedGraphExists(linkedGraph, link, isLinkedGraphLoading),
    validateLinkedNodeExists(linkedGraph, link, isLinkedGraphLoading),
    validateSimulationFrequency(currentGraph, linkedGraph),
    validateDateRange(currentGraph, linkedGraph),
    validateNodeType(linkedGraph, link, nodesMapper, nodeId),
    validateVersionConflict(link, nodesMapper),
    validateNoParentNode(nodesMapper, nodeId),
  ].filter((msg): msg is string => msg !== null)

  return {
    hasError: errorMessages.length > 0,
    errorMessages,
  }
}
